import * as ko from 'knockout';
import * as _ from "underscore";
import * as moment from 'moment';
import * as globalConfig from 'globalConfig';
import * as localisationHelper from 'localisationHelper';

// Types
import Moment = moment.Moment;

// Models
import { JoinConfiguration } from 'Content/ts/Models/Join/JoinConfiguration';
import { JoiningStepEnum } from 'Content/ts/Models/Join/JoiningStep';
import { MembershipType } from 'Content/ts/Models/Join/MembershipType';
import { PriceBreakdownDetailsModel } from 'Content/ts//Models/Shared/PriceBreakdown/PriceBreakdownDetailsModel';
import { BillingCycleEnum } from 'Content/ts/Models/Shared/Enums/BillingCycle';
import { BillingMethodEnum } from 'Content/ts/Models/Shared/Enums/BillingMethod';
import { SelectTwoWithChildren } from "Content/ts/Models/Shared/SelectTwo/selectTwoModels";
import { GLProcessTypeEnum } from 'Content/ts/Models/Shared/Enums/GLProcessType';

// Interfaces
import { IAgreementModel } from 'Content/ts/Models/Join/AgreementModels';
import { IContactServicePrice } from 'Content/ts/Models/Membership/Interfaces/IContactServicePrice';
import { IFeeModel } from 'Content/ts/Models/Join/Interfaces/IFeeModel';
import { IMemberServiceModel } from 'Content/ts/Models/Join/Interfaces/IMemberServiceModel';
import { IMemberServicesPriceModel, IMembershipPriceModel } from 'Content/ts/Models/Join/Interfaces/IMembershipPrice';
import { IMembershipModel } from 'Content/ts/Models/Join/Interfaces/IMembershipModel';
import { IMembershipBaseModel } from 'Content/ts/Models/Join/Interfaces/IMembershipBaseModel';
import { IMembershipPriceDetailsResponseModel } from 'Content/ts/Models/Join/Interfaces/IMembershipPriceDetailsResponseModel';
import { IMembershipRequestModel } from 'Content/ts/Models/Join/Interfaces/IMembershipRequestModel';
import { IMembershipPriceDetailsRequestModel } from 'Content/ts/Models/Join/Interfaces/IMembershipPriceDetailsRequestModel';
import { ISelectedAgreementPrice } from 'Content/ts/Models/Join/Interfaces/ISelectedAgreementPrice';
import { IPriceBreakdownDetailsModel } from 'Content/ts//Models/Shared/PriceBreakdown/IPriceBreakdownDetailsModel';
import { IContactServiceOptionPriceDetailsRequestModel } from 'Content/ts/Models/Membership/Interfaces/IContactServicePriceRequest';

// Components
import 'Content/ts/Shared/AgreementSelector/single-agreement-selection';
import 'Content/ts/Shared/MemberServiceSelector/single-member-service-selection';
import 'Content/ts/Shared/PriceBreakdown/price-breakdown';
import "Content/ts/Shared/BootstrapAlerts/AlertBase";
import "Content/ts/Shared/Loading/loading-base";

// Services
import { JoinService } from 'Content/ts/Join/Services/joinService';
import { ValidationService } from '../Shared/Services/Validation/validationService';
import { ContactServicesService } from 'Content/ts/Membership/Services/contactServicesService';
import { MembershipService } from 'Content/ts/Membership/Services/membershipService';
import { DateTimeFormatterService } from "Content/ts/Shared/Controls/DateTime/Services/dateTimeFormatterService";

// Functions
import { PriceDisplayModel } from 'Content/ts/Models/Shared/Pricing/pricingModels';

// Configuration
import { PricingBindings } from '../../../../../Scripts/ts/CustomBindingHandlers/Pricing/pricingBindings';

export const Deps = {
    JoinService: JoinService,
    MembershipService: MembershipService,
    ContactServicesService: ContactServicesService
}

export interface IMembershipSelectionParams {
    JoinConfig: JoinConfiguration;
}

const genders = {
    Male: 1,
    Female: 2
};

export default class MembershipSelection {
    customMessages: {
        DefaultMembershipText: string,
        DirectDebitPerMonth: string,
        PayInFull: string,
        LoadingPricingErrorMessage: string,
        MembershipValidation: string,
        GenderRestrictionMale: string;
        GenderRestrictionFemale: string;
        AgeRestrictionBetween: string;
        AgeRestrictionUnder: string;
        AgeRestrictionOver: string;
        PriceLoadingText: string;
    };

    priceCycleDescriptions: { [key: number]: string };

    configuration: JoinConfiguration;

    memberships: KnockoutObservableArray<IMembershipModel>;
    memberServiceModels: IMemberServiceModel[];
    availableMemberships: KnockoutObservableArray<SelectTwoWithChildren>;
    selectedMembershipPrice: KnockoutObservable<IMembershipPriceModel>;
    selectedMembershipPriceId: KnockoutObservable<number>;
    loadingMemberships: KnockoutObservable<boolean>;
    loadingPriceDetails: KnockoutObservable<boolean>;
    membershipPriceDetails: PriceBreakdownDetailsModel;
    selectedMembershipName: KnockoutObservable<string>;
    errorLoadingPrice: KnockoutObservable<boolean>;

    membershipSelected: KnockoutComputed<boolean>;
    membershipPriceLoaded: KnockoutComputed<boolean>;
    membershipCostText: KnockoutComputed<string>;
    membershipDescription: KnockoutComputed<string>;
    showAgreements: KnockoutComputed<boolean>;
    showMemberServices: KnockoutComputed<boolean>;

    // agreement restrictions
    genderRestrictionMessage: KnockoutComputed<string>;
    showGenderRestriction: KnockoutComputed<boolean>;
    ageRestrictionMessage: KnockoutComputed<string>;
    showAgeRestriction: KnockoutComputed<boolean>;
    showStartDatePicker: KnockoutComputed<boolean>;

    facilityId: KnockoutObservable<number>;
    validationFields: KnockoutObservable<any>[];

    subscriptions: KnockoutSubscription[];

    constructor(params: IMembershipSelectionParams) {
        this.configuration = params.JoinConfig;
        this.facilityId = this.configuration.facilityId;
        this.membershipPriceDetails = params.JoinConfig.priceDetails;
        this.selectedMembershipPrice = params.JoinConfig.selectedMembershipPrice;

        this.loadingMemberships = this.configuration.membershipsLoading;
        this.loadingPriceDetails = this.configuration.priceDetailsLoading;
        this.memberships = ko.observableArray<IMembershipModel>([]);
        this.availableMemberships = ko.observableArray<SelectTwoWithChildren>([]);
        this.selectedMembershipName = ko.observable("");
        this.errorLoadingPrice = ko.observable(false);
        this.selectedMembershipPriceId = ko.observable(null);

        this.validationFields = params.JoinConfig
            ? params.JoinConfig.getValidationFields(JoiningStepEnum.GymSelection)
            : [];

        this.configuration.collectionDateChanged.add(this.collectionDateChanged.bind(this));
        this.configuration.facilitySelected.add(this.getMemberships.bind(this));
        this.retrieveCustomMessages();
        this.setupSubscriptions();
        this.setupComputeds();
        this.setupValidationFields();
    }

    private collectionDateChanged(monthlyCollectionDay: number): void {
        if (!!monthlyCollectionDay && this.membershipPriceDetails.MCD() !== monthlyCollectionDay) {
            this.loadPrices(monthlyCollectionDay);
        }
    }

    private retrieveCustomMessages(): void {
        this.customMessages = {
            DefaultMembershipText: localisationHelper.getString("JoinResources", "SelectMembership"),
            DirectDebitPerMonth: localisationHelper.getString("JoinResources", "DirectDebitPerMonth"),
            PayInFull: localisationHelper.getString("JoinResources", "PayInFull"),
            LoadingPricingErrorMessage: localisationHelper.getString("ExceptionResources", "ApplicationException"),
            MembershipValidation: localisationHelper.getString("JoinResources", "Validation_Agreement"),
            GenderRestrictionMale: localisationHelper.getString("JoinResources", "GenderRestrictionMale"),
            GenderRestrictionFemale: localisationHelper.getString("JoinResources", "GenderRestrictionFemale"),
            AgeRestrictionBetween: localisationHelper.getString("JoinResources", "AgeRestrictionBetween"),
            AgeRestrictionUnder: localisationHelper.getString("JoinResources", "AgeRestrictionUnder"),
            AgeRestrictionOver: localisationHelper.getString("JoinResources", "AgeRestrictionOver"),
            PriceLoadingText: localisationHelper.getString("GenericResources", "PriceDetailsLoading")
        };

        this.priceCycleDescriptions = {
            [BillingCycleEnum.Weekly]: localisationHelper.getString("JoinResources", "BillCycleDesc_Weekly"),
            [BillingCycleEnum.BiWeekly]: localisationHelper.getString("JoinResources", "BillCycleDesc_BiWeekly"),
            [BillingCycleEnum.FourWeekly]: localisationHelper.getString("JoinResources", "BillCycleDesc_FourWeekly"),
            [BillingCycleEnum.Monthly]: localisationHelper.getString("JoinResources", "BillCycleDesc_Monthly"),
            [BillingCycleEnum.Annual]: localisationHelper.getString("JoinResources", "BillCycleDesc_Annual"),
            [BillingCycleEnum.Pay_In_Full]: '',
            [BillingCycleEnum.Quarterly]: localisationHelper.getString("JoinResources", "BillCycleDesc_Quarterly"),
            [BillingCycleEnum.HalfYearly]: localisationHelper.getString("JoinResources", "BillCycleDesc_HalfYearly"),
            [BillingCycleEnum.PerVisit]: localisationHelper.getString("JoinResources", "BillCycleDesc_PerVisit"),
            [BillingCycleEnum.Biennial]: localisationHelper.getString("JoinResources", "BillCycleDesc_Biennial")
        };
    }

    private getMemberships(facilityId: number): void {
        this.loadingMemberships(true);
        this.selectedMembershipPriceId(null);
        this.facilityId(facilityId);

        const requestModel = {
            JourneyId: this.configuration.journeyId,
            FacilityId: facilityId
        } as IMembershipRequestModel;

        if (this.configuration.membershipType() === MembershipType.Agreement) {
            Deps.JoinService.getAgreements(requestModel).then((agreements: IAgreementModel[]) => {
                if (agreements.length > 0) {
                    const memberships = this.agreementsToMemberships(agreements);
                    this.autoselectAndUpdateMemberships(memberships);
                }

                this.loadingMemberships(false);
            });
        } else {
            Deps.JoinService.getMemberServices(requestModel).then((memberServices: IMemberServiceModel[]) => {
                if (memberServices.length > 0) {
                    const memberships = this.memberServicesToMemberships(memberServices);
                    this.autoselectAndUpdateMemberships(memberships);
                }

                this.loadingMemberships(false);
            });
        }
    }

    private autoselectAndUpdateMemberships(memberships: IMembershipModel[]): void {
        if (this.configuration.selectedMembershipPriceId() !== null && this.configuration.selectedMembershipPriceId() !== undefined) {

            // for each membership, get the price ids
            const membershipPriceIds = _.chain(memberships).map((mem: IMembershipModel) => {
                // if the item in the array has children, go through each child item and pluck those
                if (mem.Prices && mem.Prices.length > 0) {
                    return _.pluck(mem.Prices, "Id");
                }
            }).flatten().value();

            // check the id exists in the provided list
            const membershipExists = _.contains(membershipPriceIds, this.configuration.selectedMembershipPriceId());

            // load the memberships
            this.updateMemberships(memberships);

            // set the selected agreement
            if (membershipExists) {
                this.selectedMembershipPriceId(this.configuration.selectedMembershipPriceId());
                this.configuration.selectedMembershipPriceId(null);
            }
        }
        else {
            this.updateMemberships(memberships);
        }
    }

    private setupComputeds(): void {
        this.membershipSelected = ko.computed(() => {
            return this.selectedMembershipPrice() !== null;
        });

        this.membershipPriceLoaded = ko.computed<boolean>(() => {
            return this.membershipSelected() && !this.loadingPriceDetails();
        });

        this.membershipCostText = ko.computed(() => {
            if (!this.membershipPriceDetails.HasFees()) {
                return "";
            }

            const price = PricingBindings.findPriceToDisplay(this.membershipPriceDetails.CycleFee());
            const displayPrice = `${globalConfig.CurrencySymbol}${price.toFixed(2)}`;
            const cycle = this.membershipPriceDetails.BillingCycleId() ?
                this.priceCycleDescriptions[this.membershipPriceDetails.BillingCycleId()] :
                '';

            return `${displayPrice} ${cycle} ${this.membershipPriceDetails.IsDirectDebit()
                ? this.customMessages.DirectDebitPerMonth
                : this.customMessages.PayInFull}`;
        });

        this.membershipDescription = ko.computed(() => {
            return this.membershipSelected()
                ? this.selectedMembershipPrice().Description
                : "";
        });

        this.showAgreements = ko.computed(() => {
            return this.configuration.membershipType() === MembershipType.Agreement;
        });

        this.showMemberServices = ko.computed(() => {
            return this.configuration.membershipType() === MembershipType.MemberService;
        });

        this.genderRestrictionMessage = ko.computed(() => {
            const gender = this.configuration.genderRestriction();
            let message = '';

            if (gender) {
                switch (gender.ExactValue) {
                    case genders.Male:
                        message = this.customMessages.GenderRestrictionMale;
                        break;
                    case genders.Female:
                        message = this.customMessages.GenderRestrictionFemale;
                        break;
                    default:
                        break;
                }
            }

            return message;
        });

        this.showGenderRestriction = ko.computed(() => {
            return !!this.configuration.genderRestriction();
        });

        this.ageRestrictionMessage = ko.computed(() => {
            let message = '';

            if (!!this.configuration.ageRestriction()) {
                const max = this.configuration.ageRestriction().MaxValue,
                    min = this.configuration.ageRestriction().MinValue;

                if (max > 0 && min > 0) {
                    message = this.customMessages.AgeRestrictionBetween
                        .replace('{0}', min.toString())
                        .replace('{1}', max.toString());
                }
                else if (max > 0) {
                    message = this.customMessages.AgeRestrictionUnder
                        .replace('{0}', max.toString());
                }
                else {
                    message = this.customMessages.AgeRestrictionOver
                        .replace('{0}', min.toString());
                }
            }

            return message;
        });

        this.showAgeRestriction = ko.computed(() => {
            return !!this.configuration.ageRestriction();
        });

        this.showStartDatePicker = ko.computed(() => {
            return this.configuration.enableFlexibleStartDate() && this.configuration.startDateConfigLoaded() &&
                (this.configuration.membershipStartDate() != null || this.selectedMembershipPriceId() != null);
        });
    }

    private setupValidationFields(): void {
        ValidationService.AddValidationRule(this.selectedMembershipPrice, this.validationFields, {
            required: {
                params: true,
                message: this.customMessages.MembershipValidation
            }
        });

        ValidationService.AddValidationRule(this.loadingPriceDetails, this.validationFields, {
            validation: {
                validator: function (loadingPriceDetails: boolean) {
                    return !loadingPriceDetails;
                }
            }
        });

        ValidationService.AddValidationRule(this.errorLoadingPrice, this.validationFields, {
            validation: {
                validator: function (errorLoadingPrice: boolean) {
                    return !errorLoadingPrice;
                }
            }
        });
    }

    private setupSubscriptions(): void {

        this.subscriptions = [
            this.selectedMembershipPriceId.subscribe(() => {
                this.configuration.startDateConfigLoaded(false);
                this.loadPrices()
                    .then(() => {
                        this.configureStartDates();
                    });
            }),
            this.configuration.membershipStartDateString.subscribe((newValue: string) => {
                if (newValue === null || newValue === undefined) {
                    return;
                }

                this.loadPrices();
            })
        ]
    }

    private loadPrices(monthlyCollectionDay: number = null): Promise<any> {
        const _selectedMembershipPriceId = this.selectedMembershipPriceId();

        this.errorLoadingPrice(false);

        if (!this.selectedMembershipPriceId()) {
            this.resetMembershipPrice();
            return Promise.resolve();
        }

        this.loadingPriceDetails(true);

        const membership = this.memberships().find(a => a.Prices.map(p => p.Id).includes(_selectedMembershipPriceId)),
            price = _.findWhere(membership.Prices, { Id: _selectedMembershipPriceId });

        this.selectedMembershipPrice(price);
        this.selectedMembershipName(membership.Name);

        const requestModel = {
            MCD: monthlyCollectionDay,
            ClubId: this.facilityId(),
            MembershipId: membership.Id,
            MembershipPriceId: _selectedMembershipPriceId,
            BillingMethodId: price.BillingMethodId,
            EarliestStartDate: this.configuration.membershipStartDate() != null
                                ? this.configuration.membershipStartDate().format(DateTimeFormatterService.TimeZoneAgnosticAjaxFormat)
                                : null,
        } as IMembershipPriceDetailsRequestModel;

        this.configuration.membershipPriceRequest(requestModel);

        if (this.configuration.membershipType() != MembershipType.Agreement) {
            return this.getMemberServiceOptionPriceDetails(requestModel);
        }

        const selectedPrice = {
            PriceRequest: requestModel,
            Restriction: {
                Gender: price.Gender,
                MaxAge: price.MaxAge,
                MinAge: price.MinAge
            }
        } as ISelectedAgreementPrice;

        const promise = this.agreementPriceSelected(selectedPrice);
        this.configuration.agreementSelected.dispatch();
        return promise;
    }

    private resetMembershipPrice(): void {
        this.configuration.genderRestriction(null);
        this.configuration.ageRestriction(null);
    }

    private agreementsToMemberships(agreements: IAgreementModel[]): IMembershipModel[] {
        const mappedAgreements = agreements.map(a => {
            return {
                Id: a.AgreementId,
                Name: a.Name,
                Prices: a.AgreementPrices.map(p => {
                    return {
                        MembershipId: a.AgreementId,
                        Id: p.AgreementPriceId,
                        BillingMethodId: p.BillingMethodId,
                        Name: p.Name,
                        Description: p.Description,
                        Gender: p.Gender,
                        MaxAge: p.MaxAge,
                        MinAge: p.MinAge,
                        BillingCycleId: null
                    };
                })
            };
        });

        this.availableMemberships(this.buildAgreementHierarchy(mappedAgreements, null));

        return mappedAgreements;
    }

    private buildAgreementHierarchy(agreements: Array<IMembershipBaseModel>, assignedId: number): SelectTwoWithChildren[] {
        return _.chain(agreements)
            .sortBy("Name")
            .map((agreementDetails: IMembershipModel) => {
                const options = {
                    text: agreementDetails.Name,
                    id: agreementDetails.Id.toString()
                } as SelectTwoWithChildren;

                if (agreementDetails.Prices && agreementDetails.Prices.length > 0) {
                    options.children = this.buildAgreementHierarchy(agreementDetails.Prices, assignedId);
                }
                else if (agreementDetails.Id === assignedId) {
                    options.selected = true;
                }

                return options;
            })
            .value();
    }

    private memberServicesToMemberships(memberServices: IMemberServiceModel[]): IMembershipModel[] {
        const mappedMemberServices = memberServices.map(membership => <IMembershipModel>{
            Id: membership.Id,
            Name: membership.Name,
            Prices: membership.MemberServiceOptions.map(membershipOption => <IMemberServicesPriceModel>{
                Id: membershipOption.Id,
                MembershipId: membership.Id,
                BillingMethodId: this.convertBillingCycleToBillingMethod(membershipOption.BillingCycleId),
                Name: membershipOption.Name,
                Description: membershipOption.Description,
                MaxStartDate: membershipOption.MaxStartDate,
                ServiceDateMethod: membershipOption.ServiceDateMethod
            })
        });

        this.memberServiceModels = memberServices;
        this.availableMemberships(this.buildAgreementHierarchy(mappedMemberServices, null));

        return mappedMemberServices;
    }

    private updateMemberships(memberships: IMembershipModel[]): void {
        this.memberships.removeAll();
        this.memberships.push.apply(this.memberships, memberships);
        this.selectedMembershipPrice(null);
    }

    private agreementPriceSelected(selectedPrice: ISelectedAgreementPrice): Promise<any> {
        this.configuration.genderRestriction(selectedPrice.Restriction.Gender > 0 ? {
            Identifier: 'gender',
            ExactValue: selectedPrice.Restriction.Gender,
            MaxValue: null,
            MinValue: null
        } : null);

        this.configuration.ageRestriction(selectedPrice.Restriction.MaxAge > 0 || selectedPrice.Restriction.MinAge > 0 ? {
            Identifier: 'date-of-birth',
            ExactValue: null,
            MaxValue: selectedPrice.Restriction.MaxAge,
            MinValue: selectedPrice.Restriction.MinAge
        } : null);

        return this.getAgreementPriceDetails(selectedPrice.PriceRequest);
    }

    private getAgreementPriceDetails(requestModel: IMembershipPriceDetailsRequestModel): Promise<any> {

        this.resetPrice();

        return Deps.MembershipService.getAgreementPrice(requestModel)
            .then((response: IMembershipPriceDetailsResponseModel) => {
                if (response != null) {
                    //In order to be able to be able to differentiate between automatically applied discounts and promo code discount,
                    //we have to calculate the automatic discount before the model is build so we can pass it through to price-breakdown.
                    //When a promocode is added, the discounts returned are grouped by fee type meaning we then do not know the split between auto/promo discount amount
                    const autoDiscount = response.Fees.flatMap(f => f.Discounts).filter(d => d.Amount > 0).map(f => f.Amount).reduce((a, b) => a + b, 0);
                    this.updatePrice(Deps.MembershipService.getMembershipPriceBreakdownModel(response, autoDiscount));
                }
            })
            .catch(() => {
                this.errorLoadingPrice(true);
                this.resetPrice();
            })
            .finally(() => {
                this.loadingPriceDetails(false);
            });
    }

    private getMemberServiceOptionPriceDetails(requestModel: IMembershipPriceDetailsRequestModel) : Promise<any> {
        const priceRequest = {
            ContactId: 0,
            SavePriceRequest: false,
            MCD: requestModel.MCD,
            Services: [{
                ServiceId: requestModel.MembershipId,
                ServiceOptionId: requestModel.MembershipPriceId,
                BillingMethodId: requestModel.BillingMethodId,
                ClubId: requestModel.ClubId,
                StartDate: requestModel.EarliestStartDate
            }]
        } as IContactServiceOptionPriceDetailsRequestModel;

        return Deps.ContactServicesService.getServiceOptionPrice(priceRequest)
            .then((response: IContactServicePrice[]) => {
                if (response != null) {
                    const price = response[0];

                    let cycleFee: PriceDisplayModel;
                    if (price.CycleFee == null) {
                        const pifFee = price.Fees.find(fee => fee.GLProcessTypeId == GLProcessTypeEnum.ContactServicePIF);
                        cycleFee = {
                            IsGross: pifFee.IsGross,
                            GrossAmount: pifFee.GrossAmount,
                            NetAmount: pifFee.NetAmount
                        };
                    } else {
                        cycleFee = {
                            IsGross: price.CycleFee.IsGross,
                            GrossAmount: price.CycleFee.GrossAmount,
                            NetAmount: price.CycleFee.NetAmount
                        }
                    };

                    const priceDetails: IMembershipPriceDetailsResponseModel = {
                        CycleFee: cycleFee,
                        BillingMethod: this.convertBillingCycleToBillingMethod(price.BillingCycleId),
                        BillingCycle: price.BillingCycleId,
                        MCD: price.MCD,
                        FirstBillDate: price.FirstBillDate != null ? moment(price.FirstBillDate).format() : null,
                        Fees: price.Fees.map(f => {
                            return {
                                Name: f.DisplayName.replace("Contact Service - ", ""),
                                Discounts: [{
                                    Name: f.DisplayName,
                                    Amount: f.DiscountAmount,
                                    PromoCode: null
                                }],
                                DisplayPrice: {
                                    IsGross: f.IsGross,
                                    GrossAmount: f.GrossAmount,
                                    NetAmount: f.NetAmount
                                }
                            };
                        }),
                        PriceRequestGroupId: null
                    };

                    this.updatePrice(Deps.MembershipService.getMembershipPriceBreakdownModel(priceDetails));
                }
            })
            .catch(() => {
                this.errorLoadingPrice(true);
                this.resetPrice();
            })
            .finally(() => {
                this.loadingPriceDetails(false);
            });
    }

    private convertBillingCycleToBillingMethod(billingCycle: BillingCycleEnum): BillingMethodEnum {
        switch (billingCycle) {
            case BillingCycleEnum.Monthly:
            case BillingCycleEnum.Weekly:
            case BillingCycleEnum.BiWeekly:
            case BillingCycleEnum.FourWeekly:
            case BillingCycleEnum.Annual:
            case BillingCycleEnum.Quarterly:
            case BillingCycleEnum.HalfYearly:
            case BillingCycleEnum.Biennial:
                return BillingMethodEnum.DirectDebit;
            default:
                return BillingMethodEnum.PayInFull;
        }
    }

    private updatePrice(model: IPriceBreakdownDetailsModel) {
        this.membershipPriceDetails.CycleFee(model.CycleFee);
        this.membershipPriceDetails.BillingMethodId(model.BillingMethodId);
        this.membershipPriceDetails.BillingCycleId(model.BillingCycleId);
        this.membershipPriceDetails.PriceRequestGroupId(model.PriceRequestGroupId);
        this.membershipPriceDetails.FirstBillDate(model.FirstBillDate);
        this.membershipPriceDetails.MCD(model.MCD);
        this.membershipPriceDetails.AutomaticDiscount(model.AutomaticDiscount);
        this.membershipPriceDetails.Fees(model.Fees);
    }

    private configureStartDates() {
        let membershipStartDate = this.configuration.facilityEarliestStartDate();
        let maxMemberServiceStartDate = null;
        if (this.configuration.membershipType() === MembershipType.MemberService) {

            const memberService = this.memberServiceModels.find(m => m.Id === this.configuration.membershipPriceRequest().MembershipId);
            if (memberService) {
                const memberServiceOption = memberService.MemberServiceOptions.find(o => o.Id === this.configuration.membershipPriceRequest().MembershipPriceId);
                if (memberServiceOption) {
                    membershipStartDate = moment(memberServiceOption.MaxStartDate);
                    maxMemberServiceStartDate = membershipStartDate;
                }
            }
        }
        this.configuration.maxMemberServiceStartDate(maxMemberServiceStartDate);
        this.configuration.membershipStartDate(membershipStartDate);
        this.configuration.setStartDateConfiguration();
    }

    private resetPrice() {
        this.updatePrice({
            CycleFee: null,
            FirstBillDate: null,
            PriceRequestGroupId: null,
            BillingMethodId: null,
            BillingCycleId: null,
            MCD: null,
            AutomaticDiscount: null,
            Fees: []
        });
    }

    dispose() {
        this.subscriptions.forEach(s => s.dispose());
    }
}

const moduleName = 'membership-selection';
ko.components.unregister(moduleName);
ko.components.register(moduleName, {
    viewModel: MembershipSelection,
    template: { require: `text!/Themes/Default/Content/ts/Join/${moduleName}.html` }
});